import { writable } from 'svelte/store';

// Browser detection (since $app/environment is not available in libs/core)
const browser = typeof window !== 'undefined';

/**
 * References Store
 * Tracks user responses that have been marked with data.reference in the survey YAML
 * Allows template interpolation like {{references.name}} in headers and text
 */

// Type definitions
export type ReferenceValue = string | number | boolean | string[];
export type ReferencesRecord = Record<string, ReferenceValue>;

// Constants
const REFERENCES_STORAGE_KEY = 'walku:references';

// Helper functions
function getStoredReferences(): ReferencesRecord {
  if (!browser) return {};

  try {
    const stored = sessionStorage.getItem(REFERENCES_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('Failed to load references from sessionStorage', error);
    return {};
  }
}

function saveReferences(references: ReferencesRecord): void {
  if (!browser) return;

  try {
    sessionStorage.setItem(REFERENCES_STORAGE_KEY, JSON.stringify(references));
  } catch (error) {
    console.error('Failed to save references to sessionStorage', error);
  }
}

// Create the store
function createReferencesStore() {
  const initialState = getStoredReferences();
  const { subscribe, update, set } = writable<ReferencesRecord>(initialState);

  return {
    subscribe,

    // Set a reference value
    setReference: (key: string, value: ReferenceValue) => {
      update(state => {
        const newState = {
          ...state,
          [key]: value
        };
        saveReferences(newState);
        return newState;
      });
    },

    // Get a specific reference value
    getReference: (key: string): ReferenceValue | undefined => {
      let result: ReferenceValue | undefined = undefined;
      update(state => {
        result = state[key];
        return state;
      });
      return result;
    },

    // Get all references
    getAllReferences: () => {
      let result: ReferencesRecord = {};
      update(state => {
        result = { ...state };
        return state;
      });
      return result;
    },

    // Clear all references
    clear: () => {
      if (browser) {
        sessionStorage.removeItem(REFERENCES_STORAGE_KEY);
      }
      const emptyState = {};
      set(emptyState);
      return emptyState;
    }
  };
}

// Export the singleton store instance
export const referencesStore = createReferencesStore();

// Listen for survey identity changes and clear store
if (typeof window !== 'undefined') {
  window.addEventListener('walku:clear-stores', () => {
    referencesStore.clear();
  });
}
